import { EditOutlined } from '@ant-design/icons';
import { Badge, Button, Form, List, Modal } from 'antd';
import { useMemo, useState } from 'react';

import { SCard } from 'app/components/StaycationUI';
import { computeExperienceName } from 'app/components/commons/Experience/utils';
import { Mode } from 'app/components/commons/Hotel/Types';
import { ViewEditModeContext } from 'app/context/ViewEditModeContext/ViewEditModeContext';
import { useBuilderHotelExperiences } from 'app/hooks/data/core/builder/useBuilderHotelExperiences';
import { useUpdateBuilderExperience } from 'app/hooks/data/core/builder/useUpdateBuilderExperience';
import { useExperiencesCategories } from 'app/hooks/data/useExperience';
import type { BuilderHotelExperience } from 'app/typings/core/builder/experiences';
import type { ExperienceCategory } from 'app/typings/experiences';

import { HeaderWrapper } from '../../HeaderWrapper/HeaderWrapper';
import '../ExperiencesForm.scss';

import { BuilderExperienceForm } from './BuilderExperienceForm';
import { mapExperienceToFormValues, mapFormValuesToRequest } from './mapping';
import type { BuilderExperienceFormValues } from './types';

type Props = {
  handleNext: () => void;
  hotelId: number;
  isLoading?: boolean;
};

const EDIT_MODE_CONTEXT = {
  mode: Mode.Edit,
  isEditMode: true,
  isViewMode: false,
  enterEditMode: () => {
    /* the builder is always in edit mode */
  },
  enterViewMode: () => {
    /* the builder is always in edit mode */
  },
};

type ExperienceModalProps = {
  experience: BuilderHotelExperience;
  category: ExperienceCategory;
  title: string;
  isSaving: boolean;
  onClose: () => void;
  onSave: (values: BuilderExperienceFormValues) => void;
};

// Own form instance so that each opening starts from the experience values
const ExperienceModal = ({
  experience,
  category,
  title,
  isSaving,
  onClose,
  onSave,
}: ExperienceModalProps) => {
  const [form] = Form.useForm<BuilderExperienceFormValues>();
  const initialValues = useMemo(
    () => mapExperienceToFormValues(experience),
    [experience]
  );

  return (
    <Modal
      title={title}
      width={800}
      onCancel={onClose}
      open
      className="experiences-modal"
      wrapClassName="hotel-builder-modal"
      footer={
        <>
          <Button key="cancel" onClick={onClose}>
            Cancel
          </Button>
          <Button
            key="submit"
            onClick={() => form.submit()}
            loading={isSaving}
            type="primary"
          >
            Save
          </Button>
        </>
      }
    >
      <ViewEditModeContext.Provider value={EDIT_MODE_CONTEXT}>
        <Form
          form={form}
          layout="vertical"
          initialValues={initialValues}
          onFinish={(values) => onSave({ ...initialValues, ...values })}
        >
          <BuilderExperienceForm category={category} />
        </Form>
      </ViewEditModeContext.Provider>
    </Modal>
  );
};

export const ModernExperiencesForm = ({
  handleNext,
  hotelId,
  isLoading = false,
}: Props) => {
  const [selectedExperience, setSelectedExperience] =
    useState<BuilderHotelExperience | null>(null);

  const { data } = useBuilderHotelExperiences(hotelId);
  const { mutateAsync: updateExperience, isPending: isSaving } =
    useUpdateBuilderExperience(hotelId);

  const experiences = useMemo(() => data ?? [], [data]);

  const { data: experienceCategories = [] } = useExperiencesCategories();

  const findCategory = (experience: BuilderHotelExperience) =>
    experienceCategories.find(
      (category) => category.id === experience.categoryId
    );

  const experienceName = (
    experience: BuilderHotelExperience,
    category?: ExperienceCategory
  ) =>
    computeExperienceName({
      category,
      quantity: null,
      brand: experience.brand ?? null,
      duration: experience.duration ?? null,
      durationDescription: experience.durationDescription ?? null,
      time: experience.time ?? null,
      locations: experience.location ?? null,
    });

  const handleClose = () => setSelectedExperience(null);

  const handleSave = async (
    experience: BuilderHotelExperience,
    category: ExperienceCategory,
    values: BuilderExperienceFormValues
  ) => {
    const payload = mapFormValuesToRequest(values, !!category.price);

    try {
      await updateExperience({ experienceId: experience.id, payload });
      setSelectedExperience(null);
    } catch {
      // the error message is displayed by the mutation
    }
  };

  const isExperienceFilled = (experience: BuilderHotelExperience) =>
    !!experience.coverPicture;
  const areExperiencesFilled = experiences.every(isExperienceFilled);

  const renderExperienceActionButton = (experience: BuilderHotelExperience) => {
    if (isExperienceFilled(experience)) {
      return <EditOutlined onClick={() => setSelectedExperience(experience)} />;
    }

    return (
      <Button
        onClick={() => setSelectedExperience(experience)}
        className="fill-experience-button"
      >
        Fill
      </Button>
    );
  };

  const renderExperience = (experience: BuilderHotelExperience) => (
    <List.Item className="experiences-form-list-item">
      <div>
        <Badge color={isExperienceFilled(experience) ? 'green' : 'orange'} />
        {experienceName(experience, findCategory(experience))}
      </div>

      {renderExperienceActionButton(experience)}
    </List.Item>
  );

  const category = selectedExperience && findCategory(selectedExperience);

  return (
    <HeaderWrapper
      nextDisabled={!areExperiencesFilled}
      handleNext={handleNext}
      isLoading={isLoading}
    >
      <SCard
        title="Hotel activities"
        subtitle="Fill all the activity information"
        isExpanded
        key="experiences"
        className="experiences-form-list"
      >
        <List
          size="large"
          dataSource={experiences}
          renderItem={renderExperience}
        />

        {!!selectedExperience && !!category && (
          <ExperienceModal
            key={selectedExperience.id}
            experience={selectedExperience}
            category={category}
            title={experienceName(selectedExperience, category)}
            isSaving={isSaving}
            onClose={handleClose}
            onSave={(values) =>
              handleSave(selectedExperience, category, values)
            }
          />
        )}
      </SCard>
    </HeaderWrapper>
  );
};
